import { createApp } from 'vue'
import { store } from '@/store'
import router from './router'
import App from './App.vue'
import { resolveTenant } from '@/utils/tenantUtils'
import { mark, markNavigation } from '@/utils/perfMarks'
import { createHead } from '@unhead/vue/client'
import { artistConfig } from '@/config/artist.config'
import { deleteStorageFile } from '@/utils/storageCleanup'
import { validEmail, isValidHttpUrl } from '@/utils/validation'
import ButtonCustom from '@/components/ButtonCustom'

markNavigation()
mark('boot:start')

// Let Vue Router own scroll behaviour — prevents the browser from natively
// restoring scroll position (with momentum) on hard refresh.
history.scrollRestoration = 'manual'

// Kick off tenant resolution immediately — before Vue mounts — so the
// hostname→uid lookup runs in parallel with app initialization.
resolveTenant()

const app = createApp(App)
const head = createHead()

// Set global default meta tags from artist configuration
// Individual pages can override these with their own useHead() calls
head.push({
  title: artistConfig.site?.defaultTitle || artistConfig.site?.title || 'Art Gallery',
  meta: [
    {
      name: 'description',
      content: artistConfig.site?.defaultDescription || `Explore original artwork by ${artistConfig.artist?.name || 'the artist'}`
    },
    {
      name: 'author',
      content: artistConfig.artist?.name || 'Artist'
    },
    // Open Graph tags
    {
      property: 'og:type',
      content: 'website'
    },
    {
      property: 'og:site_name',
      content: artistConfig.site?.title || artistConfig.artist?.name || 'Art Gallery'
    },
    {
      property: 'og:url',
      content: artistConfig.site?.url || 'https://example.com'
    }
  ]
})

app.component('ButtonCustom', ButtonCustom)

app.config.globalProperties.$store = store

app.mixin({
  computed: {
    allWorks() {
      return store.allWorks || null
    },
    userUid() {
      return store.user.uid || null
    },
    email() {
      return store.user.email || null
    },
    displayName() {
      return store.artistConfig?.artist?.name || 'artist'
    },
    userIsAdmin() {
      return store.user.isAdmin || null
    },
    userWorks() {
      return store.allWorks
    },
    inbox() {
      return store.inbox || []
    },
    messages() {
      return (store.inbox || []).filter(i => i.kind === 'inquiry')
    },
    contacts() {
      return (store.inbox || []).filter(i => i.kind === 'contact')
    }
  },
  methods: {
    validEmail,
    isValidHttpUrl,
    deleteFileAsPromise(file) {
      return deleteStorageFile(file)
    }
  }
})

// If you've upgraded to vue-router@4: app.use(router)
app.use(head)
app.use(router)

app.mount('#app')
